import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import MiniMap from './MiniMap';
import { getSanityImageProps } from '../../utils/sanityImage';

/**
 * LocationCard component for displaying location information in a card format
 * 
 * @param {Object} props - Component props
 * @param {Object} props.location - Location data
 * @param {string} props.location.name - Location name
 * @param {string} props.location.slug - Location slug for URL
 * @param {Object} props.location.address - Location address
 * @param {string} props.location.address.street - Street address
 * @param {string} props.location.address.city - City
 * @param {string} props.location.address.state - State
 * @param {string} props.location.imageUrl - URL to location image
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} - LocationCard component
 */
const LocationCard = memo(({ location, className = '', showMap = true, isClosest = false }) => {
  // Default image if none provided
  const imageUrl = location.imageUrl || location.featuredImage || "/images/hero/blueberry-pancakes-original-pancake-house.jpg";
  
  // Get optimized image props for Sanity images
  const imageProps = getSanityImageProps(imageUrl, 'locationCard');
  
  return (
    <Link 
      to={`/locations/${location.slug}`}
      className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow ${className}`}
    >
      <div className="relative">
        <div className="h-48 overflow-hidden">
          <img 
            src={imageProps.src || imageUrl} 
            srcSet={imageProps.srcSet}
            sizes={imageProps.sizes}
            alt={location.name} 
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        {/* Distance Badge */}
        {location.distance !== null && location.distance !== undefined && (
          <div className="absolute top-3 right-3 bg-white px-3 py-1.5 rounded-full shadow-md">
            <span className="text-sm font-semibold text-brand-navy">{location.distance} mi</span>
          </div>
        )}
        {/* Selected Badge */}
        {isClosest && (
          <div className="absolute top-3 left-3 bg-green-100 px-3 py-1.5 rounded-full shadow-md">
            <span className="text-sm font-semibold text-green-800">Selected</span>
          </div>
        )}
      </div>
      
      <div className="relative">
        {/* Content and Map in 50/50 split */}
        <div className="flex flex-row">
          {/* Left side - Content */}
          <div className={`${showMap ? 'w-3/5' : 'w-full'} p-6`}>
            <h3 className="text-lg font-bold text-brand-navy mb-2">{location.name}</h3>
            {location.address && (
              <div className="flex text-gray-600 mb-4 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1.5 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="flex flex-col">
                  {location.address.street && (
                    <span>{location.address.street}</span>
                  )}
                  <span>
                    {location.address.city}, {location.address.state}
                    {location.address.zipCode && ` ${location.address.zipCode}`}
                  </span>
                </div>
              </div>
            )}
            <div className="flex items-center text-brand-navy font-medium text-xs">
              View Details
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          {/* Right side - Map */}
          {showMap && (
            <div className="w-2/5 relative p-4 pt-0" style={{ minHeight: '120px' }}>
              {location.coordinates ? (
                <div className="relative -mt-4 pb-[100%]"> {/* Overlap the image slightly + 1:1 aspect ratio */}
                  <div className="absolute inset-0 p-1 bg-white rounded-xl shadow-md">
                    <MiniMap 
                      key={`minimap-${location._id || location.slug}`}
                      coordinates={location.coordinates} 
                      className="w-full h-full rounded-md border border-gray-200" 
                    />
                  </div>
                </div>
              ) : (
                <div className="p-6 flex items-center justify-center h-full">
                  <div className="text-gray-400 text-sm italic">Map not available</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
});

LocationCard.displayName = 'LocationCard';

LocationCard.propTypes = {
  location: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    address: PropTypes.shape({
      street: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
    }),
    coordinates: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    imageUrl: PropTypes.string,
    featuredImage: PropTypes.string,
    distance: PropTypes.number,
  }).isRequired,
  className: PropTypes.string,
  showMap: PropTypes.bool,
  isClosest: PropTypes.bool,
};

export default LocationCard;
